import React, { useEffect, useState } from "react";
import { nanoid } from "nanoid";
import { authClient } from "../../lib/auth-client";
import { Avatar, Button, Spinner, Textarea } from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";
import Toast from "../../components/Toast/Toast";
import bg from "../../assets/well_small.jpg";
import Logo from "../../components/Logo/Logo";
import axios from "axios";
import { API_URL } from "../../core/constants";
import { checkJupiterToken, setJupiterToken } from "../../core/cookie";

const SharedSecretChallenge = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [incorrectSharedSecret, setIncorrectSharedSecret] = useState(false);
  const [secret, setSecret] = useState("");

  const { data: session } = authClient.useSession();

  // Check for the cookie on component mount
  useEffect(() => {
    const jupiterToken = checkJupiterToken();
    if (jupiterToken) {
      navigate("/jupiter/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleSignOut = async () => {
    const cacheBuster = nanoid();
    await authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          navigate("/jupiter/?c=" + cacheBuster, { replace: true });
        },
      },
    });
  };

  const handleSharedSecretChange = (event) => {
    setSecret(event.target.value); // Use the memoized debounced function
  };

  const handleSubmit = () => {
    console.log("Submitting shared secret:", secret);
    setLoading(true);

    try {
      axios
        .post(
          `${API_URL}/jupiter/shared-secret/respond`,
          {
            secret: secret,
          },
          { withCredentials: true, validateStatus: () => true }
        )
        .then(function (response) {
          console.log("response", response);

          // respond to given mfa response
          if (response.status === 200) {
            //the token is valid, save it
            setJupiterToken(secret);

            //only redirect if all is juicy
            navigate(`/jupiter/dashboard`, { replace: true });
          } else {
            setLoading(false);
            setIncorrectSharedSecret(true);
            return false;
          }
        })
        .catch(function (error) {
          Toast.popError("Something has gone wrong. Please try again.");
          console.log(error);
          setLoading(false);
        });
    } catch (error) {
      console.error("Error during secret submission:", error);
      setLoading(false);
    }
  };

  const renderLoading = () => {
    return (
      <div className="flex-1">
        <h2 className="text-4xl font-bold text-center text-gray-700">
          <Logo isDark />
        </h2>
        <div className="flex justify-center items-center flex-col mt-8">
          <Spinner className="h-12 w-12" />
          <span>One moment...</span>
        </div>
      </div>
    );
  };

  const renderIncorrectSharedSecret = () => {
    return (
      <div className="flex justify-center items-center flex-col mt-8 bg-red-100 p-4 rounded-md">
        <span>
          Incorrect Secret. Please try again. Please note we will only allow a
          small number of retries.
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white">
      <div className="flex justify-center h-screen">
        <div
          className="hidden bg-cover lg:block lg:w-1/2"
          style={{
            backgroundImage: `url(${bg})`,
            backgroundPosition: "40% 50%",
          }}
        ></div>

        <div className="flex items-center w-full max-w-md px-6 mx-auto lg:w-1/2 lg:max-w-1/2">
          {loading ? (
            renderLoading()
          ) : (
            <div className="flex-1">
              <div className="text-center">
                <h2 className="text-4xl font-bold text-center text-gray-700">
                  <Logo isDark />
                </h2>

                <div className="mt-8">
                  <Avatar
                    src={
                      session?.user.image ||
                      "https://zeropa.s3.eu-west-1.amazonaws.com/blank.jpg"
                    }
                    referrerPolicy="no-referrer"
                    alt={`${session?.user.name}'s avatar`}
                    size="lg"
                  />
                  <h2 className="mt-2 text-lg">
                    Welcome back {session?.user.name}
                  </h2>

                  <p className="mt-3 text-gray-500">
                    Please enter your shared secret to continue. We will save
                    this for 90 days so you won't have to enter it again during
                    that time.
                  </p>

                  {incorrectSharedSecret && renderIncorrectSharedSecret()}
                </div>
              </div>

              <div className="mt-8 mb-8">
                <div className="flex gap-x-3 bg-gray-100 px-2 py-4 rounded-md justify-center">
                  <Textarea
                    label="Enter your shared secret"
                    value={secret}
                    onChange={handleSharedSecretChange}
                    placeholder="Enter your shared secret"
                    className="w-full bg-white"
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex flex-col w-full">
                <Button onClick={handleSubmit}>Continue</Button>
              </div>

              <div className="mt-4">
                <button
                  className="text-sm hover:cursor-pointer text-blue-600 hover:text-blue-800"
                  onClick={handleSignOut}
                >
                  Go back to login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedSecretChallenge;
